/* =========================================================================
 * IS THIS ORDER TODAY'S WORK, OR IS IT THREE YEARS OLD?
 *
 * Brian, on the home screen: "Remember what I said about needs attention.
 * Those are stale." Four rows, all reading "1071 days ago" -- late 2023 --
 * at the top of the card he opens every morning.
 *
 * 🔴 THE PATHOLOGY IS THE ONE ALREADY FIXED IN THE WORKLIST, IN A PLACE THE
 * FIX NEVER REACHED. `isOverdue()` is `dueDate < now` with NO LOWER BOUND, and
 * `sortAccounts()` ranks overdue FIRST. So the oldest unfinished order in the
 * system is not merely included, it is promoted: the further past its date it
 * drifts, the more certainly it leads the card. Stale records do not sink.
 * They rise. That was true of crowned-and-unworked championships and it is
 * true here for exactly the same reason.
 *
 * ⚠️ BUT THE RULE IS NOT THE CHAMPIONSHIP RULE, AND COPYING IT WOULD BE WRONG.
 * A championship belongs to a season because it is an EVENT on a date.
 * An order is a job with a due date, and the dates mean different things. What
 * carries across is the CONVENTION -- SR files a season Jul→Jun, so an order
 * due before the current season started is not this season's work -- not the
 * function. `recordSeasonYear` reads championship fields and would answer null
 * for every order on the board.
 *
 * ⚠️ AND THE NULL RULE CARRIES ACROSS EXACTLY. An order with NO due date is
 * not old; it is undated, and nothing places it in a season. Treating "no
 * date" as "stale" is the mistake that would have emptied an entire cheer book
 * when the same cleanup was done for championships. Undated orders are KEPT,
 * always, and counted separately so the number can be checked.
 * ========================================================================= */
import { SEASON_START_MONTH, currentSeasonYear, seasonLabel } from './championships.js'

/** The SR season an arbitrary date falls in. Jul 2026 → 2026; Mar 2026 → 2025. */
export function seasonYearOfDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null
  return d.getMonth() >= SEASON_START_MONTH ? d.getFullYear() : d.getFullYear() - 1
}

/**
 * Is this order from a concluded season?
 *
 * `true` only when the order HAS a due date and that date's season has ended.
 * Undated → false. Future → false. Those are the two directions this must not
 * get wrong, and they are asserted.
 */
export function isStaleOrder(account, now = new Date()) {
  const d = account?.dueDate
  const s = seasonYearOfDate(d)
  if (s == null) return false
  return s < currentSeasonYear(now)
}

/** How far past its due date, in whole days. Null when undated. */
export function daysOverdue(account, now = new Date()) {
  const d = account?.dueDate
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null
  return Math.floor((now - d) / 86400000)
}

/**
 * Split an attention list into what belongs on a morning screen and what does
 * not.
 *
 * ⚠️ IT RETURNS BOTH HALVES, AND THAT IS THE WHOLE POINT. Brian: "a
 * 1,071-day-old item that genuinely still needs attention is a DIFFERENT
 * finding and should be visible somewhere deliberate, not mixed into today's
 * work." A filter that merely dropped them would answer the complaint and
 * lose the information -- and nine items vanishing from a count of twelve with
 * no explanation is how a real problem disappears unnoticed.
 */
export function partitionAttention(accounts = [], now = new Date()) {
  const current = []
  const stale = []
  for (const a of accounts) (isStaleOrder(a, now) ? stale : current).push(a)
  const seasons = [...new Set(stale.map((a) => seasonYearOfDate(a?.dueDate)))]
    .filter((s) => s != null).sort((x, y) => y - x).map((s) => seasonLabel(s))
  return {
    current,
    stale,
    seasons,
    /* Undated orders are in `current` by construction; counted so the sentence
     * can say so rather than leaving a reader to wonder where they went. */
    undated: current.filter((a) => !a?.dueDate).length,
    oldestStaleDays: stale.reduce(
      (max, a) => Math.max(max, daysOverdue(a, now) ?? 0), 0,
    ) || null,
  }
}

/**
 * The reconciliation sentence. The count must always add up on screen.
 *
 * 🔴 "Never a bare number that silently shrank." If twelve becomes three, the
 * card says so and names the seasons the other nine came from.
 */
export function attentionScopeLine(part) {
  if (!part || !part.stale.length) return ''
  const n = part.stale.length.toLocaleString('en-US')
  const total = (part.current.length + part.stale.length).toLocaleString('en-US')
  const from = part.seasons.length ? ` from ${part.seasons.join(', ')}` : ''
  const oldest = part.oldestStaleDays
    ? ` The oldest is ${part.oldestStaleDays.toLocaleString('en-US')} days past its date.`
    : ''
  return `${n} of ${total} are from a concluded season${from} and are held out of today's `
    + `work.${oldest} They are still open — reachable, not hidden.`
}
